import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import makeComponentTrashable from 'trashable-react';
import useBrandContext from 'Contexts/brand-context';
import { fetchTodaysInvoiceSummary } from 'Shared/util/web-api';
import Spinner from 'Shared/components/spinner';
import { RadioGroup, RadioButton } from 'Shared/components/radio';
import { colors } from 'Shared/util/theme';
import { useTranslation } from 'Shared/util/custom-hooks';
import LandingDoughnutChart from './landing-doughnut-chart';
import InvoiceSummarySectionTable from './invoice-summary-section-table';
import { tabs, getTableItems, createSummary } from './invoice-summary-section-utils';
import styles from './invoice-summary-section.css';


const InvoiceSummarySection = ({
  registerPromise,
}) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(tabs.INVOICES_REGISTERED_TODAY);
  const [showTable, setShowTable] = useState(false);
  const [tableItems, setTableItems] = useState([]);
  const { brandData } = useBrandContext();
  const t = useTranslation(x => x.invoiceSummarySection);

  useEffect(() => {
    registerPromise(fetchTodaysInvoiceSummary())
      .then((result) => {
        setItems(result);
        setSummary(createSummary(result));
        setLoading(false);
      });
  }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setTableItems(getTableItems(items, tab));
  };

  const handleTabContentClick = (tab) => {
    handleTabClick(tab);
    setShowTable(activeTab === tab ? !showTable : true);
  };

  return (
    <>
      {loading ? <Spinner /> : (
        <div className={styles.container}>
          <div className={styles.tabContainer}>
            <RadioGroup
              name="details-group"
              value={activeTab}
              handleChange={(e) => handleTabClick(e.target.value)}
            >
              <RadioButton value={tabs.INVOICES_REGISTERED_TODAY} text={t.registered} />
              <RadioButton value={tabs.DURING_PROCESSING} text={t.processing} />
              <RadioButton value={tabs.SOLD_INVOICES_TODAY} text={t.sold} />
            </RadioGroup>
          </div>

          <div
            className={`${styles.tabContent} ${activeTab === tabs.INVOICES_REGISTERED_TODAY ? styles.activeTabContent : ''}`}
            onClick={() => handleTabContentClick(tabs.INVOICES_REGISTERED_TODAY)}
          >
            <LandingDoughnutChart
              title={t.registeredInvoicesToday}
              numberAmount={summary.invoicesRegisteredToday.numberAmount}
              sumAmount={summary.invoicesRegisteredToday.sumAmount}
              currency={summary.invoicesRegisteredToday.currencyCode}
              circleColor={brandData.name === 'PrioritetFinans' ? colors.alt2 : colors.chart.third}
              active={showTable && activeTab === tabs.INVOICES_REGISTERED_TODAY}
            />
          </div>

          <div
            className={`${styles.tabContent} ${activeTab === tabs.DURING_PROCESSING ? styles.activeTabContent : ''}`}
            onClick={() => handleTabContentClick(tabs.DURING_PROCESSING)}
          >
            <LandingDoughnutChart
              title={t.duringProcessing}
              numberAmount={summary.duringProcessing.numberAmount}
              sumAmount={summary.duringProcessing.sumAmount}
              currency={summary.duringProcessing.currencyCode}
              circleColor={brandData.name === 'PrioritetFinans' ? colors.alt1 : colors.chart.second}
              active={showTable && activeTab === tabs.DURING_PROCESSING}
            />
          </div>

          <div
            className={`${styles.tabContent} ${activeTab === tabs.SOLD_INVOICES_TODAY ? styles.activeTabContent : ''}`}
            onClick={() => handleTabContentClick(tabs.SOLD_INVOICES_TODAY)}
          >
            <LandingDoughnutChart
              title={t.soldInvoicesToday}
              numberAmount={summary.soldInvoicesToday.numberAmount}
              sumAmount={summary.soldInvoicesToday.sumAmount}
              currency={summary.soldInvoicesToday.currencyCode}
              circleColor={brandData.name === 'PrioritetFinans' ? colors.main : colors.chart.first}
              active={showTable && activeTab === tabs.SOLD_INVOICES_TODAY}
            />
          </div>

          <div className={`${styles.tableContainer} ${showTable ? styles.slideIn : styles.slideOut}`}>
            <InvoiceSummarySectionTable
              rows={tableItems}
              pageSize={5}
            />
          </div>
        </div>
      )}
    </>
  );
};

InvoiceSummarySection.propTypes = {
  registerPromise: PropTypes.func.isRequired,
};

export default makeComponentTrashable(InvoiceSummarySection);
